import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IndexingJob, IndexingJobStatus, Integrator } from '../types';
import { indexingApi, IndexingJobsResponse } from '../api/indexing';
import { integratorApi } from '../api/integrators';
import { useAuth } from '../context/AuthContext';
import IntegratorAutocomplete from '../components/IntegratorAutocomplete';

const POLL_MS = 5000;        // gdy coś się indeksuje (PENDING/RUNNING)
const IDLE_POLL_MS = 30000;  // gdy nic aktywnego — rzadziej, ale wciąż łapiemy nowe zadania

const statusLabel: Record<IndexingJobStatus, string> = {
  PENDING: 'Oczekuje',
  RUNNING: 'W trakcie',
  DONE: 'Ukończone',
  FAILED: 'Błąd',
};

const statusColor: Record<IndexingJobStatus, string> = {
  PENDING: '#949ba4',
  RUNNING: '#1e78d6',
  DONE: '#2e9956',
  FAILED: '#cf3a32',
};

// LlamaParse tiers offered for re-indexing (PDFs only), with credit cost/page.
const REINDEX_TIERS: { value: string; label: string }[] = [
  { value: 'cost_effective', label: 'cost_effective (3 kr./str.)' },
  { value: 'agentic', label: 'agentic (10 kr./str.)' },
  { value: 'agentic_plus', label: 'agentic_plus (45 kr./str.)' },
];

const tierLabel = (tier: string) => (tier === 'text' ? 'tekst (Tika)' : tier);
const isPdfJob = (j: IndexingJob) => !!j.filename && j.filename.toLowerCase().endsWith('.pdf');

const IndexingStatus: React.FC = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<IndexingJob[]>([]);
  const [globalCounts, setGlobalCounts] =
    useState<Record<IndexingJobStatus, number>>({ PENDING: 0, RUNNING: 0, DONE: 0, FAILED: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrators, setIntegrators] = useState<Integrator[]>([]);
  const [integratorFilter, setIntegratorFilter] = useState<Integrator | null>(null);

  const load = useCallback(async (): Promise<IndexingJobsResponse | null> => {
    try {
      const { data } = await indexingApi.list();
      // Zgodność wstecz: backend sprzed zmiany zwracał gołą tablicę zadań zamiast
      // { counts, jobs } — normalizujemy, żeby UI nie wybuchał przy starym kontenerze.
      const jobs: IndexingJob[] = Array.isArray(data) ? (data as IndexingJob[]) : (data.jobs ?? []);
      const counts: Record<IndexingJobStatus, number> =
        !Array.isArray(data) && data.counts
          ? data.counts
          : jobs.reduce(
              (acc, j) => { acc[j.status]++; return acc; },
              { PENDING: 0, RUNNING: 0, DONE: 0, FAILED: 0 } as Record<IndexingJobStatus, number>
            );
      setJobs(jobs);
      setGlobalCounts(counts);
      setError(null);
      return { counts, jobs };
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się pobrać listy');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const hasActive = (data: IndexingJobsResponse | null) =>
      !!data && (data.counts.PENDING > 0 || data.counts.RUNNING > 0);

    const run = async () => {
      if (cancelled) return;
      // W ukrytej karcie nie odpytujemy backendu — sprawdzimy ponownie później.
      if (document.hidden) {
        timer = setTimeout(run, IDLE_POLL_MS);
        return;
      }
      const data = await load();
      if (cancelled) return;
      timer = setTimeout(run, hasActive(data) ? POLL_MS : IDLE_POLL_MS);
    };

    run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [load]);

  useEffect(() => {
    integratorApi.list()
      .then(({ data }) => setIntegrators(data))
      .catch(() => { /* filtr po firmie po prostu nie podpowie firm */ });
  }, []);

  const retry = async (documentationId: string, tier?: string) => {
    try {
      await indexingApi.retry(documentationId, tier);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Retry nie powiódł się');
    }
  };

  if (!user?.roles?.includes('ADMIN')) {
    return <div className="empty-state">Brak uprawnień (wymagana rola ADMIN)</div>;
  }

  const visibleJobs = integratorFilter
    ? jobs.filter((j) => (j.integratorIds ?? []).includes(integratorFilter.id))
    : jobs;

  // Bez filtra: liczniki globalne z backendu (SQL COUNT, niezależne od limitu listy).
  // Z filtrem po firmie: liczymy z przefiltrowanej, załadowanej listy.
  const counts = integratorFilter
    ? visibleJobs.reduce(
        (acc, j) => {
          acc[j.status]++;
          return acc;
        },
        { PENDING: 0, RUNNING: 0, DONE: 0, FAILED: 0 } as Record<IndexingJobStatus, number>
      )
    : globalCounts;

  return (
    <div>
      <h1 className="page-title">Indeksacje dokumentacji</h1>
      <p className="item-meta" style={{ marginBottom: '1rem' }}>
        Lista odświeża się co {POLL_MS / 1000}s. Scheduler uruchamia się co 60s.
      </p>

      <div className="card" style={{ padding: '0.75rem 1.25rem', marginBottom: '1rem' }}>
        <IntegratorAutocomplete
          integrators={integrators}
          value={integratorFilter}
          onChange={setIntegratorFilter}
          label="Firma (wpisz NIP lub nazwę)"
          style={{ maxWidth: 360 }}
        />
      </div>

      <div className="card" style={{
        marginBottom: '1rem',
        padding: '1rem 1.25rem',
        display: 'flex',
        gap: '2rem',
        flexWrap: 'wrap',
      }}>
        {(['PENDING', 'RUNNING', 'DONE', 'FAILED'] as IndexingJobStatus[]).map((s) => (
          <div key={s} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              fontSize: '0.75rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: statusColor[s], display: 'inline-block',
              }} />
              {statusLabel[s]}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-text)' }}>{counts[s]}</div>
          </div>
        ))}
      </div>

      {error && <div className="card" style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie...</div>
      ) : visibleJobs.length === 0 ? (
        <div className="empty-state">
          {integratorFilter ? 'Brak zadań indeksacji dla wybranej firmy' : 'Brak zadań indeksacji'}
        </div>
      ) : (
        <div className="items-list">
          {visibleJobs.map((j) => (
            <div key={j.id} className="item-card card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {j.name || j.filename || `Dokument #${j.documentationId}`}
                  </div>
                  {j.filename && j.name && j.filename !== j.name && (
                    <div className="item-meta" style={{ fontSize: '0.75rem' }}>Plik: {j.filename}</div>
                  )}
                  <div className="item-meta" style={{ marginTop: '0.25rem' }}>
                    {j.machines.length === 0 ? (
                      <span style={{ color: 'var(--color-warning)' }}>Brak podpiętych maszyn</span>
                    ) : (
                      <>
                        Maszyny:{' '}
                        {j.machines.map((m, i) => (
                          <React.Fragment key={m.id}>
                            {i > 0 && ', '}
                            <Link to={`/machines/${m.id}`}>{m.name}</Link>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </div>
                  {j.customerNips.length > 0 && (
                    <div className="item-meta">
                      Klienci (NIP): {j.customerNips.join(', ')}
                    </div>
                  )}
                  <div className="item-meta">
                    Utworzono: {new Date(j.createdAt).toLocaleString('pl-PL')}
                    {j.finishedAt && ' · Zakończono: ' + new Date(j.finishedAt).toLocaleString('pl-PL')}
                  </div>
                </div>
                <span
                  className="badge"
                  style={{ background: statusColor[j.status], color: '#fff' }}
                >
                  {statusLabel[j.status]}
                </span>
              </div>

              <div className="item-meta" style={{ marginTop: '0.5rem' }}>
                Prób: {j.attempts}
                {j.tier && ` · Tryb: ${tierLabel(j.tier)}`}
                {j.chunksCount != null && ` · Chunków: ${j.chunksCount}`}
              </div>

              {j.errorMessage && (
                <pre
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: '#fef2f2',
                    color: '#991b1b',
                    borderRadius: 'var(--radius)',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {j.errorMessage}
                </pre>
              )}

              {isPdfJob(j) && (j.status === 'DONE' || j.status === 'FAILED') && (
                <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <select
                    className="form-select form-select-sm"
                    value=""
                    title="Przeindeksuj dokument w wybranym trybie (zastąpi obecne chunki)"
                    onChange={(e) => {
                      const v = e.target.value;
                      e.target.value = '';
                      if (!v) return;
                      retry(j.documentationId, v === '__global__' ? undefined : v);
                    }}
                  >
                    <option value="">Przeindeksuj w trybie…</option>
                    <option value="__global__">globalny (domyślny)</option>
                    {REINDEX_TIERS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {!isPdfJob(j) && j.status === 'FAILED' && (
                <button
                  className="btn btn-primary btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => retry(j.documentationId)}
                >
                  Ponów indeksację
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IndexingStatus;
